import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    RiTeamLine,
    RiAddLine,
    RiEditLine,
    RiDeleteBinLine,
    RiCloseLine,
    RiSaveLine,
    RiLoader4Line,
    RiTwitterXFill,
    RiLinkedinFill,
    RiMailLine,
    RiImageLine
} from 'react-icons/ri';
import { API_BASE_URL } from '../../config';

const ManageTeam = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        role: '',
        image_url: '',
        bio: '',
        twitter_url: '',
        linkedin_url: '',
        email: '',
        order_index: 0
    });

    const fetchMembers = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/team/read.php`);
            const data = await response.json();
            setMembers(data.records || []);
        } catch (error) {
            console.error('Error fetching team members:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMembers();
    }, []);

    const handleOpenModal = (member = null) => {
        if (member) {
            setEditingMember(member);
            setFormData({ ...member });
        } else {
            setEditingMember(null);
            setFormData({
                name: '',
                role: '',
                image_url: '',
                bio: '',
                twitter_url: '',
                linkedin_url: '',
                email: '',
                order_index: members.length
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const endpoint = editingMember ? 'update.php' : 'create.php';

        try {
            const response = await fetch(`${API_BASE_URL}/team/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editingMember ? { ...formData, id: editingMember.id } : formData)
            });

            if (response.ok) {
                setIsModalOpen(false);
                fetchMembers();
            }
        } catch (error) {
            console.error('Error saving team member:', error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to decommission this personnel file?')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/team/delete.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id })
            });

            if (response.ok) {
                fetchMembers();
            }
        } catch (error) {
            console.error('Error deleting member:', error);
        }
    };

    return (
        <div className="max-w-7xl">
            {/* Header HUD */}
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-16">
                <div>
                    <div className="flex items-center gap-3 text-emerald-600 mb-3">
                        <div className="flex gap-1">
                            <div className="w-1 h-3 bg-emerald-500 rounded-full" />
                            <div className="w-1 h-3 bg-emerald-500/30 rounded-full" />
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-[0.4em] font-sans">Identity Management Protocol</span>
                    </div>
                    <h2 className="text-5xl font-bold text-slate-900 font-display tracking-tight">Team Matrix</h2>
                </div>
                <button
                    onClick={() => handleOpenModal()}
                    className="group flex items-center gap-3 bg-slate-900 text-white pl-6 pr-8 py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-emerald-600 transition-all shadow-2xl shadow-slate-900/20 active:scale-95"
                >
                    <div className="p-1 bg-white/10 rounded-lg group-hover:rotate-90 transition-transform">
                        <RiAddLine size={18} />
                    </div>
                    Recruit New Member
                </button>
            </header>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-32 gap-4">
                    <RiLoader4Line className="animate-spin text-emerald-500 text-5xl" />
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest animate-pulse">Scanning Bio-Grid...</span>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                    {members.map((member, index) => (
                        <motion.div
                            layout
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            key={member.id}
                            className="bg-white rounded-[2.5rem] p-7 border border-slate-100 shadow-sm hover:shadow-2xl hover:shadow-emerald-900/5 transition-all group relative overflow-hidden"
                        >
                            {/* Card Decorative HUD */}
                            <div className="absolute top-0 right-0 p-4 opacity-10 font-mono text-[8px] text-slate-900">
                                IDX_00{index + 1}
                            </div>

                            <div className="flex flex-col items-center text-center">
                                <div className="relative mb-6">
                                    <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-slate-50 shadow-inner group-hover:ring-4 ring-emerald-100 transition-all duration-500">
                                        <img
                                            src={member.image_url || 'https://via.placeholder.com/150'}
                                            alt={member.name}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                    </div>
                                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full border-4 border-white flex items-center justify-center shadow-lg">
                                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                                    </div>
                                </div>

                                <h3 className="text-xl font-bold text-slate-900 font-display mb-1">{member.name}</h3>
                                <div className="px-3 py-1 bg-emerald-50 rounded-full mb-4">
                                    <span className="text-[9px] font-black text-emerald-700 uppercase tracking-widest font-sans">{member.role}</span>
                                </div>

                                <p className="text-slate-400 text-xs font-sans italic line-clamp-2 mb-8 h-8 group-hover:text-slate-600 transition-colors">
                                    "{member.bio || 'No biographic data available.'}"
                                </p>

                                <div className="grid grid-cols-2 w-full gap-3 pt-6 border-t border-slate-50">
                                    <button
                                        onClick={() => handleOpenModal(member)}
                                        className="flex items-center justify-center gap-2 py-3 bg-slate-50 text-slate-600 hover:bg-emerald-600 hover:text-white rounded-xl transition-all font-black uppercase text-[9px] tracking-widest"
                                    >
                                        <RiEditLine size={16} />
                                        Modify
                                    </button>
                                    <button
                                        onClick={() => handleDelete(member.id)}
                                        className="flex items-center justify-center gap-2 py-3 bg-slate-50 text-slate-400 hover:bg-red-500/10 hover:text-red-500 rounded-xl transition-all font-black uppercase text-[9px] tracking-widest"
                                    >
                                        <RiDeleteBinLine size={16} />
                                        Purge
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}

                    {/* Empty State / Add Card */}
                    <button
                        onClick={() => handleOpenModal()}
                        className="h-full min-h-[300px] border-2 border-dashed border-slate-200 rounded-[2.5rem] flex flex-col items-center justify-center gap-4 text-slate-300 hover:border-emerald-300 hover:text-emerald-500 hover:bg-emerald-50/30 transition-all group"
                    >
                        <div className="w-16 h-16 rounded-3xl bg-slate-50 flex items-center justify-center group-hover:bg-white group-hover:shadow-lg transition-all">
                            <RiAddLine size={32} />
                        </div>
                        <span className="font-black uppercase tracking-[0.2em] text-[10px]">Add Human Resource</span>
                    </button>
                </div>
            )}

            {/* Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsModalOpen(false)}
                            className="absolute inset-0 bg-slate-900/80 backdrop-blur-md"
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 30 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 30 }}
                            className="relative bg-white rounded-[3.5rem] shadow-2xl w-full max-w-2xl overflow-hidden border border-white/20"
                        >
                            <div className="p-10 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                                <div>
                                    <span className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.3em] font-sans">Matrix Input</span>
                                    <h3 className="text-3xl font-bold text-slate-900 font-display mt-1">
                                        {editingMember ? 'Update Personnel' : 'Initialize Profile'}
                                    </h3>
                                </div>
                                <button onClick={() => setIsModalOpen(false)} className="w-12 h-12 bg-white text-slate-400 hover:text-slate-900 hover:rotate-90 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center transition-all">
                                    <RiCloseLine size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="p-10 max-h-[65vh] overflow-y-auto custom-scrollbar">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="space-y-3">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 font-sans">Full Identification</label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full px-6 py-4 bg-slate-100/50 border border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold text-slate-900"
                                            placeholder="Dr. John Doe"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 font-sans">Operational Role</label>
                                        <input
                                            type="text"
                                            value={formData.role}
                                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                            className="w-full px-6 py-4 bg-slate-100/50 border border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold text-slate-900"
                                            placeholder="Lead Conservator"
                                            required
                                        />
                                    </div>
                                    <div className="md:col-span-2 space-y-3">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 font-sans">Visual Avatar Asset</label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={formData.image_url}
                                                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                                                className="w-full pl-14 pr-6 py-4 bg-slate-100/50 border border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold text-slate-900"
                                                placeholder="https://images.unsplash..."
                                            />
                                            <RiImageLine className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                                        </div>
                                    </div>
                                    <div className="md:col-span-2 space-y-3">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 font-sans">Personal Bio-Narrative</label>
                                        <textarea
                                            value={formData.bio}
                                            onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                                            className="w-full px-6 py-5 bg-slate-100/50 border border-transparent rounded-3xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-medium text-slate-700 min-h-[120px]"
                                            placeholder="Share their mission statement or expertise..."
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 font-sans">Social: Twitter/X</label>
                                        <input
                                            type="text"
                                            value={formData.twitter_url}
                                            onChange={(e) => setFormData({ ...formData, twitter_url: e.target.value })}
                                            className="w-full px-6 py-4 bg-slate-100/50 border border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold text-slate-900"
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 font-sans">Social: LinkedIn</label>
                                        <input
                                            type="text"
                                            value={formData.linkedin_url}
                                            onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                                            className="w-full px-6 py-4 bg-slate-100/50 border border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold text-slate-900"
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 font-sans">Contact Channel (Email)</label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className="w-full px-6 py-4 bg-slate-100/50 border border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold text-slate-900"
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 font-sans">Matrix Order Index</label>
                                        <input
                                            type="number"
                                            value={formData.order_index}
                                            onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) })}
                                            className="w-full px-6 py-4 bg-slate-100/50 border border-transparent rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-bold text-slate-900"
                                        />
                                    </div>
                                </div>

                                <div className="mt-12">
                                    <button
                                        type="submit"
                                        className="w-full bg-slate-900 text-white font-black uppercase tracking-widest text-[11px] py-6 rounded-3xl hover:bg-emerald-600 transition-all shadow-2xl shadow-emerald-500/20 flex items-center justify-center gap-4 group"
                                    >
                                        <RiSaveLine size={22} className="group-hover:translate-y-[-2px] transition-transform" />
                                        Commit Personnel Data to Matrix
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ManageTeam;
